/**
 * posLocalCache — Dexie read/write helpers for the POS offline path.
 *
 * Wraps `dexieDb` from src/dataStore/dexieSchema.js with simple,
 * POS-shaped functions. This is NOT a generic data layer — it knows
 * specifically what POS needs and doesn't pretend otherwise.
 *
 * Tables touched here:
 *   customers, inventory_items, accounts, inventory_locations,
 *   banks, branches, financial_years, company, settings (single doc),
 *   transactions, sub_transactions, stock_flow, sync_outbox
 *
 * All reads/writes are namespaced by companyId (added as a column on every
 * row) since one browser could in theory cache multiple companies' POS data
 * if the user switches. In practice we clear on company switch.
 */

import { dexieDb } from '../dataStore/dexieSchema';

// ─── Utility ────────────────────────────────────────────────────────────

/** Strip undefined values (Dexie copes but it's cleaner). */
const strip = (obj) => {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(strip).filter(v => v !== undefined);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = (v && typeof v === 'object') ? strip(v) : v;
  }
  return out;
};

// ─── Master-data cache (writes) ─────────────────────────────────────────

/**
 * Bulk replace a whole table with the latest cloud snapshot. Called by the
 * master-sync routine when online. Old rows for `companyId` are removed first
 * so deletions in the cloud propagate.
 *
 * Each row gets a `_companyId` column tag so cross-company queries are filterable.
 */
export const cacheCollection = async (tableName, companyId, rows) => {
  if (!dexieDb[tableName]) {
    console.warn(`[posLocalCache] unknown table: ${tableName}`);
    return;
  }
  const table = dexieDb[tableName];
  const tagged = rows.map(r => ({ ...strip(r), _companyId: companyId }));
  await dexieDb.transaction('rw', table, async () => {
    await table.filter(r => r._companyId === companyId).delete();
    if (tagged.length) await table.bulkPut(tagged);
  });
};

// ─── Master-data cache (reads) ──────────────────────────────────────────

export const readCollection = async (tableName, companyId, filterFn = null) => {
  if (!dexieDb[tableName]) return [];
  let coll = dexieDb[tableName].filter(r => r._companyId === companyId);
  if (filterFn) coll = coll.filter(filterFn);
  return await coll.toArray();
};

export const readDocById = async (tableName, companyId, id) => {
  if (!dexieDb[tableName]) return null;
  const row = await dexieDb[tableName].get(id);
  if (!row || row._companyId !== companyId) return null;
  return row;
};

// ─── POS save (write a fully-built sale to local) ───────────────────────

/**
 * Save a complete POS sale offline.
 *
 * @param {string} companyId
 * @param {object} bundle - { transaction, subTransactions: [], stockFlow: [] }
 * @returns {Promise<{ outboxSeq: number }>}
 *
 * Writes are atomic: transaction header + all sub-trans + all stock-flow +
 * outbox enqueue happen inside one Dexie transaction. If any step fails the
 * whole save rolls back (mirrors Firestore writeBatch semantics).
 */
export const savePOSSaleLocally = async (companyId, bundle) => {
  const { transaction, subTransactions = [], stockFlow = [] } = bundle;
  if (!transaction?.id) throw new Error('savePOSSaleLocally: transaction.id required');

  const tagged = (row) => ({ ...strip(row), _companyId: companyId });

  let outboxSeq;
  await dexieDb.transaction(
    'rw',
    [dexieDb.transactions, dexieDb.sub_transactions, dexieDb.stock_flow, dexieDb.sync_outbox],
    async () => {
      // 1. Header
      await dexieDb.transactions.put(tagged(transaction));

      // 2. Ledger lines
      if (subTransactions.length) await dexieDb.sub_transactions.bulkPut(subTransactions.map(tagged));

      // 3. Stock movements
      if (stockFlow.length) await dexieDb.stock_flow.bulkPut(stockFlow.map(tagged));

      // 4. Single outbox row — one sync attempt pushes the whole bundle
      outboxSeq = await dexieDb.sync_outbox.add({
        status: 'pending',
        targetPath: `companies/${companyId}/transactions`,
        kind: 'pos_sale',
        companyId,
        bundle: strip(bundle),
        createdAt: new Date().toISOString(),
        lastAttemptAt: null,
        retries: 0
      });
    }
  );

  return { outboxSeq };
};

// ─── POS save (mirror: when online, also write to Dexie for cache freshness) ─

/**
 * When a POS sale succeeds online, mirror it to Dexie WITHOUT enqueuing an
 * outbox entry (since it's already in the cloud). Keeps the local cache
 * up-to-date for when the user goes offline next.
 */
export const mirrorOnlinePOSSaleToLocal = async (companyId, bundle) => {
  const { transaction, subTransactions = [], stockFlow = [] } = bundle;
  if (!transaction?.id) return;

  const tagged = (row) => ({ ...strip(row), _companyId: companyId });

  try {
    await dexieDb.transaction(
      'rw',
      [dexieDb.transactions, dexieDb.sub_transactions, dexieDb.stock_flow],
      async () => {
        await dexieDb.transactions.put(tagged(transaction));
        if (subTransactions.length) await dexieDb.sub_transactions.bulkPut(subTransactions.map(tagged));
        if (stockFlow.length) await dexieDb.stock_flow.bulkPut(stockFlow.map(tagged));
      }
    );
  } catch (err) {
    // Non-fatal — the cloud write already succeeded
    console.warn('[posLocalCache] mirror to local failed:', err);
  }
};

// ─── Outbox helpers ─────────────────────────────────────────────────────

export const listPendingOutbox = async () => {
  return await dexieDb.sync_outbox
    .where('status').anyOf(['pending', 'failed'])
    .toArray();
};

export const countPendingOutbox = async () => {
  return await dexieDb.sync_outbox
    .where('status').anyOf(['pending', 'failed'])
    .count();
};

// Count unsynced (pending/failed) sales belonging to a specific shift — used to
// block shift close until the shift's offline sales have reached the cloud
// (otherwise the close totals/variance would be understated).
export const countPendingForShift = async (shiftId) => {
  if (!shiftId) return 0;
  const rows = await listPendingOutbox();
  return rows.filter(r => r?.bundle?.transaction?.shiftId === shiftId).length;
};

// The unsynced rows of one shift (pending or refused), oldest first.
export const listPendingForShift = async (shiftId) => {
  if (!shiftId) return [];
  const rows = await listPendingOutbox();
  return rows.filter(r => r?.bundle?.transaction?.shiftId === shiftId).sort((a, b) => (a.seq || 0) - (b.seq || 0));
};

/**
 * POS-01: a sale the server keeps refusing (it was printed, then refused) can
 * be taken off this till by the owner / an admin so the shift can close. It is
 * kept in the table as 'discarded' (never uploaded, never counted) for reference.
 */
export const discardOutboxRows = async (seqs, byUser) => {
  const at = new Date().toISOString();
  for (const seq of seqs || []) {
    // eslint-disable-next-line no-await-in-loop
    await dexieDb.sync_outbox.update(seq, { status: 'discarded', discardedAt: at, discardedBy: byUser || null });
  }
};

export const markOutboxSynced = async (seq) => {
  await dexieDb.sync_outbox.update(seq, {
    status: 'synced',
    syncedAt: new Date().toISOString()
  });
};

export const markOutboxFailed = async (seq, errorMessage) => {
  const row = await dexieDb.sync_outbox.get(seq);
  await dexieDb.sync_outbox.update(seq, {
    status: 'failed',
    lastAttemptAt: new Date().toISOString(),
    retries: (row?.retries || 0) + 1,
    lastError: String(errorMessage || '').slice(0, 500)
  });
};

/**
 * Clear synced outbox rows older than `daysOld` to keep the table small.
 * Pending/failed rows are never cleared.
 */
export const cleanupSyncedOutbox = async (daysOld = 7) => {
  const cutoff = Date.now() - daysOld * 24 * 60 * 60 * 1000;
  await dexieDb.sync_outbox
    .where('status').equals('synced')
    .filter(r => r.syncedAt && new Date(r.syncedAt).getTime() < cutoff)
    .delete();
};

/**
 * Reset retries on all stuck rows (status='failed' or pending with retries > 0)
 * so the next drain attempts them again from scratch. Called when the user
 * explicitly clicks "Sync now" — they shouldn't have to wait for any back-off.
 */
export const resetStuckOutboxRetries = async () => {
  const stuck = await dexieDb.sync_outbox
    .where('status').anyOf(['pending', 'failed'])
    .toArray();
  let resetCount = 0;
  for (const row of stuck) {
    if ((row.retries || 0) > 0 || row.status === 'failed') {
      await dexieDb.sync_outbox.update(row.seq, {
        status: 'pending',
        retries: 0,
        lastError: null
      });
      resetCount++;
    }
  }
  return resetCount;
};
